/**
 * Parses ISO 8601 duration format (e.g., "PT4M13S", "PT1H2M30S") to human-readable format.
 *
 * @param {string} isoDuration - ISO 8601 duration string from YouTube API
 * @returns {string} Human-readable duration (e.g., "4:13", "1:02:30")
 */
export function formatDuration(isoDuration) {
  if (!isoDuration) return '';

  // Remove PT prefix
  const duration = isoDuration.replace('PT', '');

  // Extract hours, minutes, seconds using regex
  const hoursMatch = duration.match(/(\d+)H/);
  const minutesMatch = duration.match(/(\d+)M/);
  const secondsMatch = duration.match(/(\d+)S/);

  const hours = hoursMatch ? parseInt(hoursMatch[1], 10) : 0;
  const minutes = minutesMatch ? parseInt(minutesMatch[1], 10) : 0;
  const seconds = secondsMatch ? parseInt(secondsMatch[1], 10) : 0;

  // Format based on whether hours exist
  if (hours > 0) {
    // Format: H:MM:SS
    const paddedMinutes = minutes.toString().padStart(2, '0');
    const paddedSeconds = seconds.toString().padStart(2, '0');
    return `${hours}:${paddedMinutes}:${paddedSeconds}`;
  } else if (minutes > 0) {
    // Format: M:SS
    const paddedSeconds = seconds.toString().padStart(2, '0');
    return `${minutes}:${paddedSeconds}`;
  } else {
    // Format: 0:SS
    const paddedSeconds = seconds.toString().padStart(2, '0');
    return `0:${paddedSeconds}`;
  }
}

/**
 * Formats a date for display.
 *
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date (e.g., "Jan 15, 2024")
 */
export function formatPublishDate(date) {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  const options = {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  };

  return dateObj.toLocaleDateString('en-US', options);
}

/**
 * Formats a date in relative format (e.g., "2 days ago", "3 months ago")
 *
 * @param {string|Date} date - Date to format
 * @returns {string} Relative date string
 */
export function formatRelativeDate(date) {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffInSeconds = Math.floor((now - dateObj) / 1000);

  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`;
  if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)} months ago`;
  return `${Math.floor(diffInSeconds / 31536000)} years ago`;
}
